import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Response_Processing from "../../../layout/Others/Response_Processing";
import AdminSaveHeader from "../../../components/CommonComponents/AdminSaveHeader";
import FormContainer from "../../../components/CommonComponents/FormContainer";
import LeftWrapper from "../../../components/CommonComponents/LeftWrapper";
import Wrapper from "../../../components/CommonComponents/Wrapper";
import RightWrapper from "../../../components/CommonComponents/RightWrapper";
import MessageHeading from "../../../components/CommonComponents/MessageHeading";
import MessageLabel from "../../../components/CommonComponents/MessageLabel";
import MessageBox from "../../../components/CommonComponents/MessageBox";
import MessageSubHeading from "../../../components/CommonComponents/MessageSubHeading";
import AdminCustomInput from "../../../components/CommonComponents/AdminCustomInput";
import AdminSwitch from "../../../components/CommonComponents/AdminSwitch";
import { getItemToken } from "../../../Utils/localStorageUtils";
import { IoMdInformationCircle } from "react-icons/io";
import "./AdsMonetization.css";

const AdsMonetization = () => {
  const headers = getItemToken('access_token');
  
  const [formData, setFormData] = useState({
    adveristment_partner_commission_percentage: 60.0,
    admin_partner_commission_percentage: 40.0,
    ads_payment_status: false
  });

  const [formDataError, setFormDataError] = useState({});
  const [showOverlay, setShowOverlay] = useState(false);
  const [withOutRefresh, setWithOutRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs for form validation focus
  const advPartnerCommissionRef = useRef(null);
  const adminPartnerCommissionRef = useRef(null);

  // Calculate total commission and remaining percentage
  const totalCommission = parseFloat(formData?.adveristment_partner_commission_percentage || 0) + 
                         parseFloat(formData?.admin_partner_commission_percentage || 0);
  const remainingPercentage = (100 - totalCommission).toFixed(2);

  const formValidation = () => {
    let formIsValid = true;
    const errors = {};

    // Validate advertisement partner commission
    if (!formData?.adveristment_partner_commission_percentage && formData?.adveristment_partner_commission_percentage !== 0) {
      errors.adveristment_partner_commission_percentage = "Advertisement partner commission percentage is required.";
      if (advPartnerCommissionRef.current) {
        advPartnerCommissionRef.current.focus();
      }
      formIsValid = false;
    } else if (formData?.adveristment_partner_commission_percentage < 0 || formData?.adveristment_partner_commission_percentage > 100) {
      errors.adveristment_partner_commission_percentage = "Commission percentage must be between 0 and 100.";
      if (advPartnerCommissionRef.current) {
        advPartnerCommissionRef.current.focus();
      }
      formIsValid = false;
    }

    // Validate admin partner commission
    if (!formData?.admin_partner_commission_percentage && formData?.admin_partner_commission_percentage !== 0) {
      errors.admin_partner_commission_percentage = "Admin partner commission percentage is required.";
      if (adminPartnerCommissionRef.current && !errors.adveristment_partner_commission_percentage) {
        adminPartnerCommissionRef.current.focus();
      }
      formIsValid = false;
    } else if (formData?.admin_partner_commission_percentage < 0 || formData?.admin_partner_commission_percentage > 100) {
      errors.admin_partner_commission_percentage = "Commission percentage must be between 0 and 100.";
      if (adminPartnerCommissionRef.current && !errors.adveristment_partner_commission_percentage) {
        adminPartnerCommissionRef.current.focus();
      }
      formIsValid = false;
    }

    // Validate total commission
    if (totalCommission > 100) {
      errors.adveristment_partner_commission_percentage = "Combined commission percentages cannot exceed 100%.";
      errors.admin_partner_commission_percentage = "Combined commission percentages cannot exceed 100%.";
      if (advPartnerCommissionRef.current) {
        advPartnerCommissionRef.current.focus();
      }
      formIsValid = false;
    }

    setFormDataError(errors);
    return formIsValid;
  };

  const handleStore = async () => {
    if (formValidation()) {
      await handleUpdate();
    } else {
      console.log("Form validation failed");
    }
  };

  const handleUpdate = async () => {
    setShowOverlay(true);
    try {
      const updateData = {
        adveristment_partner_commission_percentage: parseFloat(formData?.adveristment_partner_commission_percentage || 0),
        admin_partner_commission_percentage: parseFloat(formData?.admin_partner_commission_percentage || 0),
        ads_payment_status: Boolean(formData?.ads_payment_status)
      };

      const response = await axios.patch(
        `${process.env.REACT_APP_Baseurl}/admin/advertisement/monetization-update`,
        updateData,
        { headers: headers }
      );

      const result = response?.data;
      if (result?.status) {
        setWithOutRefresh(!withOutRefresh);
        setShowOverlay(false);
        toast.success(result?.message || "ADS Monetization settings updated successfully.");
      } else {
        setShowOverlay(false);
        toast.error(result?.message || "Failed to update ADS Monetization settings.");
      }
    } catch (err) {
      setShowOverlay(false);
      const errorMessage = err?.response?.data?.message || "An error occurred while updating settings.";
      toast.error(errorMessage);
      console.error("Update error:", err);
    }
  };

  const loadCurrentSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_Baseurl}/admin/advertisement/monetization`,
        { headers: headers }
      );

      const result = response?.data;
      if (result?.status && result?.data) {
        setFormData({
          adveristment_partner_commission_percentage: result.data.adveristment_partner_commission_percentage || 60.0,
          admin_partner_commission_percentage: result.data.admin_partner_commission_percentage || 40.0,
          ads_payment_status: Boolean(result.data.ads_payment_status)
        });
        setIsInitialized(true);
      } else {
        // Use default values if no settings found
        setFormData({
          adveristment_partner_commission_percentage: 60.0,
          admin_partner_commission_percentage: 40.0,
          ads_payment_status: false
        });
        setIsInitialized(true);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading settings:", error);
      setLoading(false);
      toast.error("Failed to load ADS Monetization settings.");
    }
  };

  useEffect(() => {
    loadCurrentSettings();
  }, [withOutRefresh]);

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    if (type === "number") {
      processedValue = parseFloat(value) || 0;
      // Ensure value is within 0-100 range
      if (processedValue < 0) processedValue = 0;
      if (processedValue > 100) processedValue = 100;
    } else if (type === "checkbox") {
      processedValue = checked;
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: processedValue,
    }));

    // Clear errors when user starts typing
    setFormDataError((prevFormData) => ({
      ...prevFormData,
      [name]: "",
    }));
  };

  const handleSwitchChange = (e, id) => {
    const checked = e.target.checked;
    setFormData((prevFormData) => ({
      ...prevFormData,
      ads_payment_status: checked,
    }));
  };

  if (loading) {
    return <Response_Processing />;
  }

  return (
    <div>
      {showOverlay && <Response_Processing />}
      
      <AdminSaveHeader 
        heading="ADS Monetization Settings" 
        saveText="Update Settings" 
        saveMethod={handleStore} 
      />

      <FormContainer>
        <LeftWrapper>
          <Wrapper className="w-100">
            <MessageHeading text="Commission Configuration" />
            
            {/* Advertisement Partner Commission */}
            <MessageLabel text="Advertisement Partner Commission (%)" className="mandatorySimple" />
            <AdminCustomInput
              type="number"
              step="0.01"
              min="0"
              max="100"
              id="adveristment_partner_commission_percentage"
              name="adveristment_partner_commission_percentage"
              placeHolder="Enter advertisement partner commission percentage"
              value={formData?.adveristment_partner_commission_percentage || ""}
              ref={advPartnerCommissionRef}
              onChange={handleInput}
              error={formDataError?.adveristment_partner_commission_percentage}
            />

            {/* Admin Partner Commission */}
            <MessageLabel text="Admin Partner Commission (%)" className="mandatorySimple" />
            <AdminCustomInput
              type="number"
              step="0.01"
              min="0"
              max="100"
              id="admin_partner_commission_percentage"
              name="admin_partner_commission_percentage"
              placeHolder="Enter admin partner commission percentage"
              value={formData?.admin_partner_commission_percentage || ""}
              ref={adminPartnerCommissionRef}
              onChange={handleInput}
              error={formDataError?.admin_partner_commission_percentage}
            />

            {/* Payment Status Toggle */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="col-8 pe-2">
                <MessageLabel text="Advertisement Payment Status" />
              </div>
              <div className="col-4 text-end">
                <AdminSwitch
                  name="ads_payment_status"
                  checked={Boolean(formData?.ads_payment_status)}
                  onChange={handleSwitchChange}
                  id="ads_payment_status"
                />
              </div>
            </div>
          </Wrapper>
        </LeftWrapper>

        <RightWrapper>
          <Wrapper>
            <MessageHeading text="Commission Summary" />
            
            {/* Summary Cards */}
            <div className="mb-3 p-3 border rounded theme-bg-light">
              <div className="row">
                <div className="col-md-6">
                  <MessageSubHeading text="Advertisement Partner" />
                  <MessageBox 
                    text={`${formData?.adveristment_partner_commission_percentage || 0}%`} 
                    classname="theme-color-blue fw-bold h4"
                  />
                </div>
                <div className="col-md-6">
                  <MessageSubHeading text="Admin Partner" />
                  <MessageBox 
                    text={`${formData?.admin_partner_commission_percentage || 0}%`} 
                    classname="theme-color-green fw-bold h4"
                  />
                </div>
              </div>
            </div>

            <div className="mb-3 p-3 border rounded theme-bg-light">
              <div className="row">
                <div className="col-md-6">
                  <MessageSubHeading text="Total Commission" />
                  <MessageBox 
                    text={`${totalCommission.toFixed(2)}%`} 
                    classname={totalCommission > 100 ? "text-danger fw-bold h4" : "theme-color-primary fw-bold h4"}
                  />
                </div>
                <div className="col-md-6">
                  <MessageSubHeading text="Remaining" />
                  <MessageBox 
                    text={`${remainingPercentage}%`} 
                    classname={remainingPercentage < 0 ? "text-danger fw-bold h4" : "theme-color-success fw-bold h4"}
                  />
                </div>
              </div>
            </div>

            {/* Status Indicator */}
            <div className="mb-3 p-3 border rounded">
              <div className="d-flex align-items-center">
                <IoMdInformationCircle 
                  className={formData?.ads_payment_status ? "text-success" : "text-warning"} 
                  size={24} 
                />
                <div className="ms-3">
                  <MessageSubHeading text="Payment Status" />
                  <MessageBox 
                    text={formData?.ads_payment_status ? "Payments are enabled" : "Payments are disabled"} 
                    classname={formData?.ads_payment_status ? "text-success" : "text-warning"}
                  />
                </div>
              </div>
            </div>

            {/* Validation Warnings */}
            {totalCommission > 100 && (
              <div className="alert alert-danger" role="alert">
                <strong>Warning!</strong> Total commission exceeds 100%. Please adjust the percentages.
              </div>
            )}

            {totalCommission === 100 && (
              <div className="alert alert-success" role="alert">
                <strong>Perfect!</strong> Commission percentages add up to exactly 100%.
              </div>
            )}
          </Wrapper>
        </RightWrapper>
      </FormContainer>
    </div>
  );
};

export default AdsMonetization;